import React, { useEffect, useMemo, useState } from 'react';
import { Switch } from './payment-types/ui';
import { Coins, Percent, Save, Info, AlertCircle, Lock, FileText, X } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import SearchableDropdown from '../SearchableDropdown';

import { RubyGemMark } from '../RubyGemMark';
interface Props {
  tenantId: string;
  locationId: string;
  gateway: any;
  onSaved: () => void;
}

const CardProcessingOptionsCard: React.FC<Props> = ({ tenantId, locationId, gateway, onSaved }) => {
  const agreements: any[] = useMemo(
    () => (Array.isArray(gateway?.surchargeAgreements) ? gateway.surchargeAgreements : []),
    [gateway],
  );

  const [tipsEnabled, setTipsEnabled] = useState<boolean>(!!gateway?.tipsEnabled);
  /* Tips on CASH — a separate grant. The one above grants a prompt the card
     READER raises and is meaningless without one; a cash tip is asked on our
     own customer display and needs no reader at all. */
  const [cashTipsEnabled, setCashTipsEnabled] = useState<boolean>(!!gateway?.cashTipsEnabled);
  const [surchargeEnabled, setSurchargeEnabled] = useState<boolean>(!!gateway?.surchargeEnabled);
  const [activeAgreementId, setActiveAgreementId] = useState<string | null>(gateway?.activeSurchargeAgreementId || null);
  const [saving, setSaving] = useState(false);
  const [showNoAgreementPopup, setShowNoAgreementPopup] = useState(false);

  useEffect(() => {
    setTipsEnabled(!!gateway?.tipsEnabled);
    setCashTipsEnabled(!!gateway?.cashTipsEnabled);
    setSurchargeEnabled(!!gateway?.surchargeEnabled);
    setActiveAgreementId(gateway?.activeSurchargeAgreementId || null);
  }, [gateway]);

  const selectedAgreement = useMemo(
    () => agreements.find((a) => a.id === activeAgreementId) || null,
    [agreements, activeAgreementId],
  );

  // Display rate/flat: from selected agreement (auto-filled) or current persisted ceilings.
  const displayMaxRate = selectedAgreement ? Number(selectedAgreement.maxRate || 0) : Number(gateway?.maxSurchargeRate || 0);
  const displayMaxFlat = selectedAgreement ? Number(selectedAgreement.maxFlat || 0) : Number(gateway?.maxSurchargeFlat || 0);

  const isDirty =
    tipsEnabled !== !!gateway?.tipsEnabled ||
    cashTipsEnabled !== !!gateway?.cashTipsEnabled ||
    surchargeEnabled !== !!gateway?.surchargeEnabled ||
    activeAgreementId !== (gateway?.activeSurchargeAgreementId || null);

  const handleSave = async () => {
    try {
      setSaving(true);
      if (surchargeEnabled && !activeAgreementId) {
        toastService.error('Select an active agreement before enabling surcharge');
        return;
      }
      // Send the per-agreement caps as the new ceilings — the agreement is the
      // source of truth. Backend also re-seeds these on set-active, but pushing
      // them here covers the case where SA changed selection without using the
      // agreement-level set-active button.
      // With NO agreement selected the ceilings are left out entirely — sending
      // 0/0 wiped the stored caps every time a tips grant was saved.
      await api.posNmi.putPaymentGateway(tenantId, locationId, {
        tipsEnabled,
        cashTipsEnabled,
        surchargeEnabled,
        ...(selectedAgreement ? {
          maxSurchargeRate: Number(selectedAgreement.maxRate || 0),
          maxSurchargeFlat: Number(selectedAgreement.maxFlat || 0),
        } : {}),
        activeSurchargeAgreementId: activeAgreementId,
      });
      toastService.success('Card processing settings saved');
      onSaved();
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm text-stone-800">Card Processing</h3>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-medium">Location-wide</span>
        </div>
        {isDirty && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? <RubyGemMark size={14} drawing color="currentColor" /> : <Save size={14} />}
            Save
          </button>
        )}
      </div>
      <p className="text-xs text-stone-500 mb-4">Tips and surcharge capabilities granted to tenant. Edit anywhere — applies to all terminals at this location.</p>

      {/* Tips capability row */}
      <div className={`flex items-center gap-3 p-3 rounded-xl border mb-3 ${
        tipsEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-stone-50 border-stone-100'
      }`}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
          tipsEnabled ? 'bg-emerald-100' : 'bg-stone-100'
        }`}>
          <Coins size={16} className={tipsEnabled ? 'text-emerald-600' : 'text-stone-400'} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${tipsEnabled ? 'text-emerald-800' : 'text-stone-700'}`}>
            Tips on Card Reader
          </p>
          <p className="text-xs text-stone-500 mt-0.5">
            Grants tenant the ability to prompt customers for a tip on the payment device.
          </p>
        </div>
        <Toggle on={tipsEnabled} onChange={setTipsEnabled} label="Allow tips on card" />
      </div>

      {/* Cash tips capability row — beside the reader one, because they are
          the same KIND of grant made about different tenders. The merchant's
          percentages are shared: they set them once and they apply wherever
          tipping is granted. */}
      <div className={`flex items-center gap-3 p-3 rounded-xl border mb-3 ${
        cashTipsEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-stone-50 border-stone-100'
      }`}>
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
          cashTipsEnabled ? 'bg-emerald-100' : 'bg-stone-100'
        }`}>
          <Coins size={16} className={cashTipsEnabled ? 'text-emerald-600' : 'text-stone-400'} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${cashTipsEnabled ? 'text-emerald-800' : 'text-stone-700'}`}>
            Tips on Cash Sales
          </p>
          <p className="text-xs text-stone-500 mt-0.5">
            Asked on the customer display, using the same percentages the merchant already set. Needs no card reader.
          </p>
        </div>
        <Toggle on={cashTipsEnabled} onChange={setCashTipsEnabled} label="Allow tips on cash" />
      </div>

      {/* Surcharge capability + nested config */}
      <div className={`rounded-xl border ${
        surchargeEnabled ? 'bg-emerald-50 border-emerald-200' : 'bg-stone-50 border-stone-100'
      }`}>
        <div className="flex items-center gap-3 p-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
            surchargeEnabled ? 'bg-emerald-100' : 'bg-stone-100'
          }`}>
            <Percent size={16} className={surchargeEnabled ? 'text-emerald-600' : 'text-stone-400'} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${surchargeEnabled ? 'text-emerald-800' : 'text-stone-700'}`}>
              Credit Card Surcharge
            </p>
            <p className="text-xs text-stone-500 mt-0.5">
              Pass processing costs to the customer. Maximum allowed values come from the active agreement.
            </p>
          </div>
          <Toggle
            on={surchargeEnabled}
            onChange={(v) => {
              if (v && agreements.length === 0) {
                setShowNoAgreementPopup(true);
                return;
              }
              setSurchargeEnabled(v);
            }}
          />
        </div>

        {surchargeEnabled && agreements.length > 0 && (
          <div className="border-t border-emerald-200 p-4 bg-white rounded-b-xl space-y-4">
            {/* Active Agreement selector */}
            <div>
              <label className="block text-xs font-medium text-stone-700 mb-1">Active Agreement</label>
              <SearchableDropdown
                options={agreements.map((a) => ({
                  value: a.id,
                  label: `${a.agreementName || a.fileName} — ${Number(a.maxRate || 0).toFixed(2)}% + $${Number(a.maxFlat || 0).toFixed(2)}${a.version ? ` (${a.version})` : ''}`,
                }))}
                value={activeAgreementId}
                onChange={setActiveAgreementId}
                placeholder="— Select an agreement —"
                searchPlaceholder="Search agreements..."
              />
              <p className="text-[11px] text-stone-500 mt-1">Selecting an agreement auto-fills the maximum allowed surcharge below.</p>
            </div>

            {/* Auto-filled caps (read-only, sourced from selected agreement) */}
            {selectedAgreement && (
              <div className="rounded-lg bg-stone-50 border border-stone-200 p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Lock size={12} className="text-stone-400" />
                  <p className="text-[11px] uppercase tracking-wide text-stone-500 font-medium">Maximum Allowed Surcharge (from agreement)</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] text-stone-500">Max Rate</p>
                    <p className="text-base font-semibold text-stone-800">{displayMaxRate.toFixed(2)}%</p>
                  </div>
                  <div>
                    <p className="text-[11px] text-stone-500">Max Flat fee</p>
                    <p className="text-base font-semibold text-stone-800">${displayMaxFlat.toFixed(2)}</p>
                  </div>
                </div>
                <p className="text-[10px] text-stone-500 mt-2">
                  The rate is set per location: the merchant can go lower from any terminal's Payment Types tab in their back office, and it applies to every till at this location. It can never exceed these.
                </p>
              </div>
            )}

            {selectedAgreement && (
              <div className="flex items-start gap-2 p-2 rounded-lg bg-blue-50 border border-blue-200">
                <Info size={14} className="text-blue-700 shrink-0 mt-0.5" />
                <p className="text-[11px] text-blue-800">
                  Surcharge is calculated on <span className="font-semibold">subtotal only</span> (excludes tax and tip) per card brand rules.
                  A $25.50 sale at {displayMaxRate.toFixed(2)}% + ${displayMaxFlat.toFixed(2)} = ${(25.50 * (1 + displayMaxRate / 100) + displayMaxFlat).toFixed(2)} total.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {showNoAgreementPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
            <div className="flex items-start gap-3 p-5 border-b border-stone-100">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                <AlertCircle size={18} className="text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-stone-900 font-semibold text-base">Surcharge Agreement Required</h3>
                <p className="text-stone-500 text-xs mt-0.5">No agreements have been uploaded for this location yet.</p>
              </div>
              <button
                onClick={() => setShowNoAgreementPopup(false)}
                className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-stone-700">
                Please <span className="font-semibold">create and upload a Surcharge Agreement</span> (with its max rate and flat fee values) before enabling Credit Card Surcharge for this location.
              </p>
              <p className="text-xs text-stone-500 mt-3">
                Scroll up to the <span className="font-semibold text-stone-700">Surcharge Agreements</span> section to upload a signed PDF, then come back here to enable surcharge.
              </p>
            </div>
            <div className="p-4 border-t border-stone-100 bg-stone-50 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowNoAgreementPopup(false)}
                className="px-4 py-2 text-sm font-medium text-stone-600 hover:text-stone-800"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setShowNoAgreementPopup(false);
                  document.getElementById('surcharge-agreements')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
              >
                <FileText size={14} />
                Go to Agreements
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Exported so every card-processing screen uses one switch rather than a
 *  second one that drifts from it. Now the payment-types Switch: ruby on,
 *  44px hit target, visibly disabled. */
export const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void; disabled?: boolean; label?: string }> = ({ on, onChange, disabled, label }) => (
  <Switch on={on} onChange={onChange} disabled={disabled} label={label || (on ? 'On' : 'Off')} />
);

export default CardProcessingOptionsCard;
